import { useEffect, useState } from "react";
import { api, errText, isLateRsvp, fmtSwedishDate, copyText, API, getToken } from "@/lib/api";
import { toast } from "sonner";
import { Copy, Pencil, Trash2, Plus, Check, X, Minus, Clock, BellRing, MessageCircle, FileSpreadsheet, FileText } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const emptyForm = { name: "", plus_one_name: "", attending: null, plus_one_attending: null, dietary: "", allergies: "", notes: "" };

const StatusBadge = ({ value }) => {
  if (value === true)
    return (
      <span className="inline-flex items-center gap-1 text-[0.7rem] px-2 py-0.5 rounded-full" style={{ backgroundColor: "#e4ecdd", color: "#3c5a2e" }}>
        <Check className="w-3 h-3" /> Kommer
      </span>
    );
  if (value === false)
    return (
      <span className="inline-flex items-center gap-1 text-[0.7rem] px-2 py-0.5 rounded-full" style={{ backgroundColor: "#f0dede", color: "#8b3a3a" }}>
        <X className="w-3 h-3" /> Kommer inte
      </span>
    );
  return (
    <span className="inline-flex items-center gap-1 text-[0.7rem] px-2 py-0.5 rounded-full" style={{ backgroundColor: "#ece7dd", color: "#6b655e" }}>
      <Minus className="w-3 h-3" /> Väntar
    </span>
  );
};

const TriSelect = ({ value, onChange }) => (
  <select
    className="input-line font-sans-d text-sm"
    value={value === true ? "yes" : value === false ? "no" : "pending"}
    onChange={(e) => onChange(e.target.value === "yes" ? true : e.target.value === "no" ? false : null)}
  >
    <option value="pending">Väntar</option>
    <option value="yes">Kommer</option>
    <option value="no">Kommer inte</option>
  </select>
);

export default function GuestsTab() {
  const [guests, setGuests] = useState([]);
  const [stats, setStats] = useState(null);
  const [settings, setSettings] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const load = async () => {
    const [g, s, st] = await Promise.all([api.get("/admin/guests"), api.get("/admin/stats"), api.get("/settings")]);
    setGuests(g.data);
    setStats(s.data);
    setSettings(st.data);
  };

  useEffect(() => {
    load();
  }, []);

  const openAdd = () => {
    setEditing(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const openEdit = (g) => {
    setEditing(g);
    setForm({ ...emptyForm, ...g, plus_one_name: g.plus_one_name || "" });
    setDialogOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Namn krävs");
    try {
      if (editing) await api.put(`/admin/guests/${editing.id}`, form);
      else await api.post("/admin/guests", form);
      setDialogOpen(false);
      toast.success(editing ? "Gäst uppdaterad" : "Gäst tillagd");
      load();
    } catch (e) {
      toast.error(errText(e));
    }
  };

  const remove = async (g) => {
    if (!window.confirm(`Ta bort ${g.name}?`)) return;
    await api.delete(`/admin/guests/${g.id}`);
    toast.success("Gäst borttagen");
    load();
  };

  const copyLink = async (g) => {
    const ok = await copyText(`${window.location.origin}/invite/${g.token}`);
    ok ? toast.success("Inbjudningslänk kopierad") : toast.error("Kunde inte kopiera — markera länken manuellt");
  };

  const reminderText = (g) => {
    const names = g.plus_one_name ? `${g.name} & ${g.plus_one_name}` : g.name;
    const deadline = settings?.rsvp_deadline ? `\n\nVi hoppas av hela våra hjärtan att ni kan vara med, svara gärna senast ${fmtSwedishDate(settings.rsvp_deadline)}.` : "";
    return `Kära ${names}!\n\nVi vill så gärna fira vår stora dag tillsammans med er. Här är er personliga inbjudan till vårt bröllop den ${settings?.wedding_date} på ${settings?.venue_name}:\n\n${window.location.origin}/invite/${g.token}${deadline}\n\nMed all vår kärlek,\n${settings?.couple_name_1} & ${settings?.couple_name_2}`;
  };

  const copyReminder = async (g) => {
    const ok = await copyText(reminderText(g));
    ok
      ? toast.success(`Påminnelse till ${g.name} kopierad — klistra in i WhatsApp/SMS`)
      : toast.error("Kunde inte kopiera");
  };

  const pending = guests.filter((g) => g.rsvp_at == null);

  return (
    <div>
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8">
          {[
            { label: "Kommer", value: `${stats.attending} / ${stats.total}` },
            { label: "Kommer inte", value: stats.declined },
            { label: "Väntar på svar", value: stats.pending },
            { label: "Uppladdade foton", value: stats.photos },
          ].map((s) => (
            <div key={s.label} className="p-4" style={{ border: "1px solid rgba(212,185,160,0.5)", backgroundColor: "rgba(255,255,255,0.5)" }}>
              <p className="text-2xl font-serif-d font-semibold" style={{ color: "var(--ink)" }} data-testid={`stat-${s.label}`}>
                {s.value}
              </p>
              <p className="text-[0.65rem] uppercase tracking-widest mt-1" style={{ color: "var(--ink-soft)" }}>
                {s.label}
              </p>
            </div>
          ))}
        </div>
      )}

      {pending.length > 0 && (
        <div className="mb-8 p-4" style={{ border: "1px solid rgba(212,185,160,0.6)", backgroundColor: "rgba(243,230,211,0.35)" }} data-testid="reminder-panel">
          <div className="flex items-center gap-2 mb-3">
            <BellRing className="w-4 h-4" style={{ color: "var(--gold)" }} />
            <p className="text-sm font-medium">
              Väntar på svar ({pending.length}) — klicka för att kopiera en färdig påminnelse
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {pending.map((g) => (
              <div key={g.id} className="flex items-center" style={{ border: "1px solid rgba(212,185,160,0.7)", backgroundColor: "rgba(255,255,255,0.7)" }}>
                <button
                  className="px-3 py-1.5 text-xs hover:bg-black/5 transition-colors flex items-center gap-1.5"
                  onClick={() => copyReminder(g)}
                  title="Kopiera påminnelsemeddelande"
                  data-testid={`remind-copy-${g.name}`}
                >
                  <Copy className="w-3 h-3 opacity-50" />
                  {g.name}
                  {g.plus_one_name ? ` & ${g.plus_one_name}` : ""}
                </button>
                <a
                  className="px-2 py-1.5 hover:bg-black/5 transition-colors"
                  style={{ borderLeft: "1px solid rgba(212,185,160,0.7)" }}
                  href={`https://wa.me/?text=${encodeURIComponent(reminderText(g))}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  title="Skicka via WhatsApp"
                  data-testid={`remind-whatsapp-${g.name}`}
                >
                  <MessageCircle className="w-3.5 h-3.5" style={{ color: "#3c7a4e" }} />
                </a>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <h2 className="font-serif-d text-xl font-semibold">Gästlista</h2>
        <div className="flex items-center gap-2">
          <a
            href={`${API}/admin/guests/export?auth=${getToken()}`}
            className="btn-invite text-[0.6rem] flex-1 sm:flex-none justify-center whitespace-nowrap"
            style={{ padding: "0.55rem 0.9rem", letterSpacing: "0.15em", minWidth: 0 }}
            data-testid="export-guests-excel-btn"
            title="Ladda ner gästlistan som Excel"
          >
            <FileSpreadsheet className="w-3.5 h-3.5 shrink-0" /> Excel
          </a>
          <a
            href={`${API}/admin/guests/export.pdf?auth=${getToken()}`}
            className="btn-invite text-[0.6rem] flex-1 sm:flex-none justify-center whitespace-nowrap"
            style={{ padding: "0.55rem 0.9rem", letterSpacing: "0.15em", minWidth: 0 }}
            data-testid="export-guests-pdf-btn"
            title="Ladda ner gästlistan som PDF"
          >
            <FileText className="w-3.5 h-3.5 shrink-0" /> PDF
          </a>
          <button className="btn-invite btn-solid text-[0.6rem] flex-1 sm:flex-none justify-center whitespace-nowrap" style={{ padding: "0.55rem 0.9rem", letterSpacing: "0.15em", minWidth: 0 }} onClick={openAdd} data-testid="add-guest-btn">
            <Plus className="w-3.5 h-3.5 shrink-0" /> <span className="hidden sm:inline">Lägg till&nbsp;</span>Gäst
          </button>
        </div>
      </div>

      <div className="space-y-2">
        {guests.map((g) => (
          <div
            key={g.id}
            className="flex flex-wrap items-center gap-x-4 gap-y-2 p-4"
            style={{ border: "1px solid rgba(212,185,160,0.5)", backgroundColor: "rgba(255,255,255,0.5)" }}
            data-testid={`guest-row-${g.name}`}
          >
            <div className="flex-1 min-w-[160px]">
              <p className="font-serif-d text-lg font-medium leading-tight">{g.name}</p>
              {g.plus_one_name && (
                <p className="text-xs" style={{ color: "var(--ink-soft)" }}>
                  +1: {g.plus_one_name}
                </p>
              )}
              {(g.dietary || g.allergies) && (
                <p className="text-[0.7rem] mt-1 italic" style={{ color: "var(--ink-soft)" }}>
                  {[g.dietary, g.allergies].filter(Boolean).join(" · ")}
                </p>
              )}
            </div>
            <div className="flex flex-col gap-1 items-start">
              <StatusBadge value={g.attending} />
              {g.plus_one_name && <StatusBadge value={g.plus_one_attending} />}
              {isLateRsvp(g.rsvp_at, settings?.rsvp_deadline) && (
                <span
                  className="inline-flex items-center gap-1 text-[0.7rem] px-2 py-0.5 rounded-full"
                  style={{ backgroundColor: "#f3e6d3", color: "#8a5a2a" }}
                  data-testid={`late-rsvp-${g.name}`}
                >
                  <Clock className="w-3 h-3" /> Sent svar
                </span>
              )}
            </div>
            <div className="flex gap-1">
              <button className="p-2 opacity-60 hover:opacity-100 transition-opacity" title="Kopiera länk" onClick={() => copyLink(g)} data-testid={`copy-link-${g.name}`}>
                <Copy className="w-4 h-4" />
              </button>
              <button className="p-2 opacity-60 hover:opacity-100 transition-opacity" title="Redigera" onClick={() => openEdit(g)} data-testid={`edit-guest-${g.name}`}>
                <Pencil className="w-4 h-4" />
              </button>
              <button className="p-2 opacity-60 hover:opacity-100 transition-opacity" title="Ta bort" onClick={() => remove(g)} data-testid={`delete-guest-${g.name}`}>
                <Trash2 className="w-4 h-4" style={{ color: "var(--burgundy)" }} />
              </button>
            </div>
          </div>
        ))}
        {guests.length === 0 && (
          <p className="text-sm italic text-center py-8" style={{ color: "var(--ink-soft)" }}>
            Inga gäster ännu.
          </p>
        )}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="font-sans-d" style={{ backgroundColor: "var(--paper)" }}>
          <DialogHeader>
            <DialogTitle className="font-serif-d">{editing ? "Redigera gäst" : "Lägg till gäst"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <input className="input-line" placeholder="Namn *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="guest-name-input" />
            <input
              className="input-line"
              placeholder="+1 namn (lämna tomt om ingen)"
              value={form.plus_one_name}
              onChange={(e) => setForm({ ...form, plus_one_name: e.target.value })}
              data-testid="guest-plusone-input"
            />
            {editing && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-[0.65rem] uppercase tracking-widest mb-1" style={{ color: "var(--ink-soft)" }}>
                      {form.name || "Gäst"}
                    </p>
                    <TriSelect value={form.attending} onChange={(v) => setForm({ ...form, attending: v })} />
                  </div>
                  {form.plus_one_name && (
                    <div>
                      <p className="text-[0.65rem] uppercase tracking-widest mb-1" style={{ color: "var(--ink-soft)" }}>
                        {form.plus_one_name}
                      </p>
                      <TriSelect value={form.plus_one_attending} onChange={(v) => setForm({ ...form, plus_one_attending: v })} />
                    </div>
                  )}
                </div>
                <input className="input-line" placeholder="Kostpreferenser" value={form.dietary} onChange={(e) => setForm({ ...form, dietary: e.target.value })} />
                <input className="input-line" placeholder="Allergier" value={form.allergies} onChange={(e) => setForm({ ...form, allergies: e.target.value })} />
              </>
            )}
            <input className="input-line" placeholder="Anteckningar" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            <button className="btn-invite btn-solid w-full" onClick={save} data-testid="save-guest-btn">
              Spara
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
